import BusinessIcon from '@mui/icons-material/Business';
import PeopleIcon from '@mui/icons-material/People';
import { Box, CardContent, Chip, Stack, Typography, useTheme } from '@mui/material';
import { ELinks } from '@sharedInterfaces/globalEnums';
import { getLanguageTexts } from '@sharedInterfaces/Language/languageHelper';
import { BaseWidget } from '@src/App/Components/Widgets/BaseWidget';
import type { AppState } from '@store/store';
import React from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

export default function AdminUserOverviewWidget() {
    const theme = useTheme();
    const lang = useSelector((state: AppState) => state.employee.language);
    const employees = useSelector((state: AppState) => state.company.allEmployees);
    const orgUnits = useSelector((state: AppState) => state.OUs);
    const featureFlags = useSelector((state: AppState) => state.company.featureFlags);
    const langStrings = getLanguageTexts(lang).orgUnit;

    const overviewStats = [
        {
            label: langStrings.totalUsers,
            value: employees.length,
            icon: <PeopleIcon sx={{ fontSize: 20, color: 'primary.main' }} />,
            color: 'primary.main',
        },
        {
            label: langStrings.organizationUnits,
            value: orgUnits.length,
            icon: <BusinessIcon sx={{ fontSize: 20, color: 'secondary.main' }} />,
            color: 'secondary.main',
        },
    ];

    const navigate = useNavigate();

    const orgUnitsWithMembers = orgUnits.map((orgUnit) => ({
        id: orgUnit.id,
        name: orgUnit.title,
        memberCount: Math.floor(Math.random() * 15) + 1, // Mock data - echte Verknüpfung noch nicht implementiert
    }));

    const handleOrgUnitClick = (orgUnitId: number) => {
        if (featureFlags.organizations) {
            navigate(`/${ELinks.PEOPLE_ORGUNITS}/${orgUnitId}`);
        }
    };

    return (
        <BaseWidget>
            {/* Header */}
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    p: 2,
                    height: { xs: 50, sm: 60, md: 65 },
                    borderBottom: `1px solid ${theme.palette.divider}`,
                    background: `linear-gradient(135deg, ${theme.palette.primary.main}20 0%, ${theme.palette.primary.dark}20 100%)`,
                    backdropFilter: 'blur(10px)',
                }}
            >
                <BusinessIcon color="primary" sx={{ mr: 1 }} />
                <Typography variant="h6" sx={{ fontWeight: 600, mr: 1 }}>
                    Benutzer-Übersicht
                </Typography>
                <Chip label="Live" size="small" color="info" variant="outlined" />
            </Box>

            <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column', p: { xs: 2, sm: 2 } }}>
                {/* Overview Stats */}
                <Stack spacing={2} sx={{ mb: 3 }}>
                    {overviewStats.map((stat, index) => (
                        <Box
                            key={index}
                            sx={{
                                p: 2,
                                borderRadius: theme.shape.borderRadius,
                                border: `1px solid ${theme.palette.divider}`,
                                background: `linear-gradient(135deg, ${stat.color}08 0%, ${stat.color}04 100%)`,
                            }}
                        >
                            <Stack direction="row" alignItems="center" spacing={2}>
                                <Box
                                    sx={{
                                        p: 1,
                                        borderRadius: 2,
                                        background: `linear-gradient(135deg, ${stat.color}20 0%, ${stat.color}10 100%)`,
                                        display: 'flex',
                                        alignItems: 'center',
                                    }}
                                >
                                    {stat.icon}
                                </Box>
                                <Box sx={{ flex: 1 }}>
                                    <Typography variant="body2" color="text.secondary">
                                        {stat.label}
                                    </Typography>
                                    <Typography variant="h5" sx={{ fontWeight: 600, color: stat.color }}>
                                        {stat.value}
                                    </Typography>
                                </Box>
                            </Stack>
                        </Box>
                    ))}
                </Stack>

                {/* Organization Units List */}
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2 }}>
                    {langStrings.organizationUnits}
                </Typography>

                <Stack spacing={1}>
                    {orgUnitsWithMembers.length > 0 ? (
                        orgUnitsWithMembers.map((orgUnit, index) => (
                            <Box
                                key={index}
                                onClick={featureFlags.organizations ? () => handleOrgUnitClick(orgUnit.id) : undefined}
                                sx={{
                                    p: 1.5,
                                    borderRadius: theme.shape.borderRadius,
                                    border: `1px solid ${theme.palette.divider}`,
                                    cursor: featureFlags.organizations ? 'pointer' : 'default',
                                    '&:hover': featureFlags.organizations
                                        ? {
                                              background: `${theme.palette.primary.main}08`,
                                              borderColor: theme.palette.primary.main + '40',
                                          }
                                        : {},
                                    transition: 'all 0.2s ease',
                                    opacity: featureFlags.organizations ? 1 : 0.6,
                                }}
                            >
                                <Stack direction="row" alignItems="center" justifyContent="space-between">
                                    <Box sx={{ flex: 1 }}>
                                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                            {orgUnit.name}
                                        </Typography>
                                    </Box>
                                    <Chip
                                        label={`${orgUnit.memberCount} Mitglieder`}
                                        size="small"
                                        variant="outlined"
                                        color="primary"
                                        sx={{ fontSize: '0.7rem' }}
                                    />
                                </Stack>
                            </Box>
                        ))
                    ) : (
                        <Box
                            sx={{
                                p: 2,
                                textAlign: 'center',
                                color: 'text.secondary',
                                fontStyle: 'italic',
                            }}
                        >
                            <Typography variant="body2">Keine Organisationseinheiten verfügbar</Typography>
                        </Box>
                    )}
                </Stack>
            </CardContent>
        </BaseWidget>
    );
}
